import { useState, useEffect, useCallback } from 'react';
import { Box, Flex, Text, Grid, chakra, Dialog } from '@chakra-ui/react';
import { api } from '@/lib/api';
import { useToast } from '@/components/ui/Toast';
import { useExplain } from '@/hooks/useExplain';
import { ExplainDialog } from '@/components/ExplainDialog';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  THRESHOLD_DEFS,
  ENGINE_CONSTANTS as EC,
  ENGINE_WEIGHTS_NO_POSITION,
  ENGINE_WEIGHTS_WITH_POSITION,
  OPPORTUNITY_WEIGHTS,
  INSIDER_CONSTANTS,
  WATCHLIST_LIMITS,
} from '@market-buddy/shared';
import type { PortfolioGoal } from '@market-buddy/shared';

interface BrokerAccount {
  cash: number;
  total: number;
  ppl: number;
  result: number;
}

interface BrokerHealth {
  healthy: boolean;
  mode: string;
}

interface AutoTradeConfig {
  enabled: boolean;
  maxPositionSize: number;
  riskPercent: number;
}

interface SystemStatus {
  claude: { configured: boolean; model: string | null };
  broker: { configured: boolean; mode: string };
  cron: {
    pollingEnabled: boolean;
    edgarEnabled: boolean;
    lastCronRun: number | null;
    lastScanRun: number | null;
  };
}

// ---------------------------------------------------------------------------
// Model parameter definitions (derived from shared constants)
// ---------------------------------------------------------------------------

interface ParamDef {
  key: string;
  label: string;
  value: string;
}

interface ParamCategory {
  title: string;
  params: ParamDef[];
}

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

const MODEL_CATEGORIES: ParamCategory[] = [
  {
    title: 'Regime Detection',
    params: [
      { key: 'isTrending.entry', label: 'Trending ADX entry', value: `${THRESHOLD_DEFS.isTrending.entry} (ADX ${(THRESHOLD_DEFS.isTrending.entry * 60).toFixed(0)})` },
      { key: 'isTrending.exit', label: 'Trending ADX exit', value: `${THRESHOLD_DEFS.isTrending.exit} (ADX ${(THRESHOLD_DEFS.isTrending.exit * 60).toFixed(0)})` },
      { key: 'isDead.entry', label: 'Dead market ADX entry', value: `${THRESHOLD_DEFS.isDead.entry} (ADX ${(THRESHOLD_DEFS.isDead.entry * 60).toFixed(0)})` },
      { key: 'isDead.exit', label: 'Dead market ADX exit', value: `${THRESHOLD_DEFS.isDead.exit} (ADX ${(THRESHOLD_DEFS.isDead.exit * 60).toFixed(0)})` },
      { key: 'isVolatile.entry', label: 'Volatile ATR ratio entry', value: String(THRESHOLD_DEFS.isVolatile.entry) },
      { key: 'isVolatile.exit', label: 'Volatile ATR ratio exit', value: String(THRESHOLD_DEFS.isVolatile.exit) },
      { key: 'RANGING_MIN_ATR_PCT', label: 'Ranging min ATR%', value: pct(EC.RANGING_MIN_ATR_PCT) },
    ],
  },
  {
    title: 'Risk Scoring',
    params: [
      { key: 'RISK_SCORE_ALPHA', label: 'Smoothing alpha (EMA)', value: String(EC.RISK_SCORE_ALPHA) },
      { key: 'RISK_SCORE_DEAD_ZONE', label: 'Dead zone threshold', value: String(EC.RISK_SCORE_DEAD_ZONE) },
      { key: 'RISK_BAND_LOW_MAX', label: 'Low band max', value: String(EC.RISK_BAND_LOW_MAX) },
      { key: 'RISK_BAND_MODERATE_ENTRY', label: 'Moderate band entry', value: String(EC.RISK_BAND_MODERATE_ENTRY) },
      { key: 'RISK_BAND_ELEVATED_ENTRY', label: 'Elevated band entry', value: String(EC.RISK_BAND_ELEVATED_ENTRY) },
      { key: 'RISK_BAND_ELEVATED_EXIT', label: 'Elevated band exit', value: String(EC.RISK_BAND_ELEVATED_EXIT) },
      { key: 'RISK_BAND_HIGH_ENTRY', label: 'High band entry', value: String(EC.RISK_BAND_HIGH_ENTRY) },
      { key: 'RISK_BAND_HIGH_EXIT', label: 'High band exit', value: String(EC.RISK_BAND_HIGH_EXIT) },
    ],
  },
  {
    title: 'Risk Weights — No Position',
    params: Object.entries(ENGINE_WEIGHTS_NO_POSITION).map(([k, v]) => ({
      key: `weight.nop.${k}`,
      label: camelToTitle(k),
      value: pct(v),
    })),
  },
  {
    title: 'Risk Weights — With Position',
    params: Object.entries(ENGINE_WEIGHTS_WITH_POSITION).map(([k, v]) => ({
      key: `weight.pos.${k}`,
      label: camelToTitle(k),
      value: pct(v),
    })),
  },
  {
    title: 'Opportunity Scoring',
    params: Object.entries(OPPORTUNITY_WEIGHTS).map(([k, v]) => ({
      key: `opp.${k}`,
      label: camelToTitle(k),
      value: pct(v),
    })),
  },
];

const SCAN_CATEGORIES: ParamCategory[] = [
  {
    title: 'Market Data Polling',
    params: [
      { key: 'schedule', label: 'Cron schedule', value: '*/5 14-16,19-20 * * 1-5 (every 5 min, Mon-Fri 14:00-16:55 and 19:00-20:55 UTC)' },
      { key: 'coverage', label: 'Session coverage', value: 'US peak hours: open + first 2h, pre-close' },
      { key: 'groups', label: 'Round-robin groups', value: '2 groups (rotated each tick to stay within API budget)' },
      { key: 'delay', label: 'Inter-symbol delay', value: '8 seconds (rate-limits Twelve Data calls)' },
      { key: 'budget', label: 'API budget', value: '8 credits/min, 800/day (Twelve Data free tier)' },
      { key: 'maxInstruments', label: 'Max instruments', value: String(WATCHLIST_LIMITS.MAX_INSTRUMENTS) },
      { key: 'quoteFetches', label: 'Quote fetches per tick', value: '3 max (Finnhub, separate budget)' },
      { key: 'killSwitch', label: 'Kill switch', value: 'CONFIG_STORE key: config:polling' },
    ],
  },
  {
    title: 'Instrument Maturity',
    params: [
      { key: 'seeding', label: 'Seeding stage', value: `0-${WATCHLIST_LIMITS.MATURITY_SEEDING} candles (~6h). No engine output.` },
      { key: 'warming', label: 'Warming stage', value: `${WATCHLIST_LIMITS.MATURITY_SEEDING}-${WATCHLIST_LIMITS.MATURITY_WARMING} candles (~13h). Provisional analysis, no cards.` },
      { key: 'active', label: 'Active stage', value: `${WATCHLIST_LIMITS.MATURITY_WARMING}+ candles. Full operation, cards emitted.` },
      { key: 'rationale', label: 'Why wait?', value: 'SMA(50) and ADX(14) need ~200 candles to stabilise' },
    ],
  },
  {
    title: 'SEC EDGAR Insider Scanner',
    params: [
      { key: 'edgarSchedule', label: 'Cron schedule', value: '0 22 * * 1-5 (daily at 22:00 UTC, Mon-Fri)' },
      { key: 'source', label: 'Data source', value: 'SEC EDGAR full-text search API (Form 4 filings)' },
      { key: 'filter', label: 'Transaction filter', value: 'Open-market purchases only (code P), excludes automatic plans' },
      { key: 'clusterWindow', label: 'Cluster window', value: `${INSIDER_CONSTANTS.CLUSTER_WINDOW_DAYS} days` },
      { key: 'clusterMin', label: 'Cluster min insiders', value: `${INSIDER_CONSTANTS.CLUSTER_MIN_INSIDERS} distinct insiders` },
      { key: 'largePurchase', label: 'Large purchase threshold', value: `$${(INSIDER_CONSTANTS.LARGE_PURCHASE_THRESHOLD / 1000).toFixed(0)}K` },
      { key: 'ownershipThreshold', label: 'Ownership increase threshold', value: `${(INSIDER_CONSTANTS.OWNERSHIP_INCREASE_THRESHOLD * 100).toFixed(0)}%` },
      { key: 'signalDecay', label: 'Signal decay', value: `Linear over ${INSIDER_CONSTANTS.SIGNAL_DECAY_DAYS} days to zero` },
      { key: 'oppWeight', label: 'Opportunity score weight', value: pct(OPPORTUNITY_WEIGHTS.insider_signal) + ' (max +12 pts)' },
      { key: 'rateLimit', label: 'EDGAR rate limit', value: `${INSIDER_CONSTANTS.EDGAR_REQUEST_DELAY_MS}ms between requests (<10 req/s)` },
      { key: 'circuitBreaker', label: 'Circuit breaker', value: `Stops after ${INSIDER_CONSTANTS.EDGAR_MAX_FAILURES} consecutive failures` },
      { key: 'cleanup', label: 'Data retention', value: `Transactions: ${INSIDER_CONSTANTS.CLEANUP_TRANSACTIONS_DAYS} days, Clusters: ${INSIDER_CONSTANTS.CLEANUP_CLUSTERS_DAYS} days` },
      { key: 'edgarKillSwitch', label: 'Kill switch', value: 'CONFIG_STORE key: config:edgar_scanner' },
    ],
  },
];

function camelToTitle(s: string): string {
  return s
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Settings Component
// ---------------------------------------------------------------------------

export function Settings() {
  const { toast } = useToast();
  const [account, setAccount] = useState<BrokerAccount | null>(null);
  const [health, setHealth] = useState<BrokerHealth | null>(null);
  const [autoTrade, setAutoTrade] = useState<AutoTradeConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  // Editable auto-trade params
  const [editMaxPos, setEditMaxPos] = useState(10);
  const [editRisk, setEditRisk] = useState(1.0);
  const [saving, setSaving] = useState(false);

  // System status
  const [status, setStatus] = useState<SystemStatus | null>(null);

  // Reset
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  // Auto-trade enable confirmation
  const [confirmEnableOpen, setConfirmEnableOpen] = useState(false);

  // Scanner trigger
  const [scanning, setScanning] = useState(false);

  // Portfolio goal
  const [goal, setGoal] = useState<PortfolioGoal | null>(null);
  const [editGoalTarget, setEditGoalTarget] = useState('20000');
  const [editGoalDate, setEditGoalDate] = useState('2027-03-31');
  const [goalSaving, setGoalSaving] = useState(false);

  // Model config collapsed state
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // AI explain dialog
  const { open: explainOpen, setOpen: setExplainOpen, param: explainParam, text: explainText, loading: explainLoading, explain } = useExplain();

  const fetchAll = useCallback(async () => {
    try {
      const [accountRes, healthRes, atRes, goalRes, statusRes] = await Promise.allSettled([
        api.get<BrokerAccount>('/api/broker/account'),
        api.get<BrokerHealth>('/api/broker/health'),
        api.get<AutoTradeConfig>('/api/broker/auto-trade'),
        api.get<PortfolioGoal | null>('/api/goals'),
        api.get<SystemStatus>('/api/admin/status'),
      ]);
      if (accountRes.status === 'fulfilled') setAccount(accountRes.value);
      if (healthRes.status === 'fulfilled') setHealth(healthRes.value);
      if (atRes.status === 'fulfilled') {
        setAutoTrade(atRes.value);
        setEditMaxPos(atRes.value.maxPositionSize);
        setEditRisk(atRes.value.riskPercent);
      }
      if (goalRes.status === 'fulfilled' && goalRes.value) {
        setGoal(goalRes.value);
        setEditGoalTarget(String(goalRes.value.targetValue));
        setEditGoalDate(goalRes.value.targetDate);
      }
      if (statusRes.status === 'fulfilled') setStatus(statusRes.value);
    } catch {
      // partial failures are fine
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function performAutoTradeToggle() {
    if (!autoTrade) return;
    setToggling(true);
    try {
      const result = await api.post<AutoTradeConfig>('/api/broker/auto-trade', {
        enabled: !autoTrade.enabled,
      });
      setAutoTrade(result);
      toast(result.enabled ? 'Auto-trade enabled' : 'Auto-trade disabled', result.enabled ? 'success' : 'info');
    } catch {
      toast('Failed to update auto-trade setting', 'error');
    } finally {
      setToggling(false);
      setConfirmEnableOpen(false);
    }
  }

  function toggleAutoTrade() {
    if (!autoTrade) return;
    // Disabling stays one click; ENABLING requires an explicit confirm — it was
    // deliberately switched off after a failed measurement week (13 Jul 2026).
    if (autoTrade.enabled) {
      performAutoTradeToggle();
    } else {
      setConfirmEnableOpen(true);
    }
  }

  async function saveTradeParams() {
    setSaving(true);
    try {
      const result = await api.post<AutoTradeConfig>('/api/broker/auto-trade', {
        maxPositionSize: editMaxPos,
        riskPercent: editRisk,
      });
      setAutoTrade(result);
      toast('Trade parameters saved', 'success');
    } catch {
      toast('Failed to save trade parameters', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function saveGoal() {
    const target = parseFloat(editGoalTarget);
    if (!target || target <= 0) {
      toast('Enter a valid target amount', 'error');
      return;
    }
    if (!editGoalDate || !/^\d{4}-\d{2}-\d{2}$/.test(editGoalDate)) {
      toast('Enter a valid target date', 'error');
      return;
    }
    setGoalSaving(true);
    try {
      const result = await api.put<PortfolioGoal>('/api/goals', {
        targetValue: target,
        targetDate: editGoalDate,
      });
      setGoal(result);
      toast('Portfolio goal saved', 'success');
    } catch {
      toast('Failed to save goal', 'error');
    } finally {
      setGoalSaving(false);
    }
  }

  async function clearGoal() {
    setGoalSaving(true);
    try {
      await api.delete('/api/goals');
      setGoal(null);
      setEditGoalTarget('20000');
      setEditGoalDate('2027-03-31');
      toast('Portfolio goal cleared', 'info');
    } catch {
      toast('Failed to clear goal', 'error');
    } finally {
      setGoalSaving(false);
    }
  }

  async function triggerScan() {
    setScanning(true);
    try {
      const result = await api.post<{ portfolio_health?: { score: number }; suggest_add?: unknown[] } | null>('/api/scanner/trigger', {});
      if (result) {
        const added = (result.suggest_add ?? []).length;
        toast(`Scan complete! Health: ${result.portfolio_health?.score ?? '?'}/100, ${added} instruments suggested.`, 'success');
      } else {
        toast('Scan skipped — rate limited or no API key configured.', 'info');
      }
    } catch {
      toast('Failed to run scanner', 'error');
    } finally {
      setScanning(false);
    }
  }

  async function resetAccount() {
    setResetting(true);
    try {
      await api.post('/api/admin/reset', {});
      toast('Account data reset successfully. Refresh the page.', 'success');
      setConfirmReset(false);
    } catch {
      toast('Failed to reset account data', 'error');
    } finally {
      setResetting(false);
    }
  }

  function toggleCategory(title: string) {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  }

  const isDemo = health?.mode === 'demo';
  const paramsChanged = autoTrade && (editMaxPos !== autoTrade.maxPositionSize || editRisk !== autoTrade.riskPercent);

  return (
    <Box>
      <Text
        fontSize="30px"
        lineHeight="38px"
        fontWeight="600"
        color="text.primary"
        mb="6"
      >
        Settings
      </Text>

      <Flex direction="column" gap="4" maxW="600px">
        {/* Broker Connection */}
        <Card>
          <CardHeader>
            <Text fontSize="lg" color="text.primary">Broker Connection</Text>
          </CardHeader>
          <CardBody>
            {loading ? (
              <Flex direction="column" gap="3">
                {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} height={20} />)}
              </Flex>
            ) : (
              <Flex direction="column" gap="3">
                <Row label="Status">
                  {health?.healthy ? (
                    <Badge variant="success">Connected</Badge>
                  ) : (
                    <Badge variant="error">Disconnected</Badge>
                  )}
                </Row>
                <Row label="Mode">
                  <Badge variant={isDemo ? 'warning' : 'error'}>
                    {isDemo ? 'Demo' : 'Live'}
                  </Badge>
                </Row>
                <Row label="Platform">
                  <Text fontSize="sm" color="text.primary">Trading 212</Text>
                </Row>
              </Flex>
            )}
          </CardBody>
        </Card>

        {/* System Status */}
        <Card>
          <CardHeader>
            <Text fontSize="lg" color="text.primary">System Status</Text>
          </CardHeader>
          <CardBody>
            {loading ? (
              <Flex direction="column" gap="3">
                {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={20} />)}
              </Flex>
            ) : status ? (
              <Flex direction="column" gap="3">
                <Row label="Claude API">
                  {status.claude.configured ? (
                    <Flex align="center" gap="2">
                      <Badge variant="success">Connected</Badge>
                      <Text fontSize="12px" color="text.tertiary">{status.claude.model}</Text>
                    </Flex>
                  ) : (
                    <Badge variant="error">Not configured</Badge>
                  )}
                </Row>
                {!status.claude.configured && (
                  <Box
                    p="3"
                    bg="status.warning.bg"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="status.warning.border"
                  >
                    <Text fontSize="sm" color="status.warning.text" m={0}>
                      Run <Text as="code" fontSize="xs">wrangler secret put ANTHROPIC_API_KEY</Text> to
                      enable Claude-powered analysis, decision evaluation, and daily portfolio scans.
                    </Text>
                  </Box>
                )}
                <Row label="Market data polling">
                  <Badge variant={status.cron.pollingEnabled ? 'success' : 'warning'}>
                    {status.cron.pollingEnabled ? 'Active' : 'Paused'}
                  </Badge>
                </Row>
                <Row label="EDGAR insider scanner">
                  <Badge variant={status.cron.edgarEnabled ? 'success' : 'warning'}>
                    {status.cron.edgarEnabled ? 'Active' : 'Disabled'}
                  </Badge>
                </Row>
                <Row label="Last cron run">
                  <Text fontSize="13px" color="text.primary">
                    {status.cron.lastCronRun
                      ? new Date(status.cron.lastCronRun).toLocaleString()
                      : 'No runs recorded'}
                  </Text>
                </Row>
                <Row label="Last daily scan">
                  <Text fontSize="13px" color="text.primary">
                    {status.cron.lastScanRun
                      ? new Date(status.cron.lastScanRun).toLocaleString()
                      : 'No scans recorded'}
                  </Text>
                </Row>
              </Flex>
            ) : (
              <Text fontSize="sm" color="text.tertiary">Unable to fetch system status</Text>
            )}
          </CardBody>
        </Card>

        {/* Scanner Trigger */}
        <Card>
          <CardHeader>
            <Text fontSize="lg" color="text.primary">Portfolio Scanner</Text>
          </CardHeader>
          <CardBody>
            <Text fontSize="sm" color="text.secondary" mb="3">
              Run Claude's daily portfolio scan now. This analyses your watchlist, suggests
              instruments to add or remove, and auto-applies the changes. Normally runs
              automatically at 22:00 UTC.
            </Text>
            <Flex align="center" gap="3">
              <Button
                onClick={triggerScan}
                loading={scanning}
                variant="primary"
                px="3" py="2" fontSize="13px"
              >
                Run Scanner Now
              </Button>
              {status?.cron.lastScanRun && (
                <Text fontSize="12px" color="text.tertiary">
                  Last run: {new Date(status.cron.lastScanRun).toLocaleString()}
                </Text>
              )}
            </Flex>
          </CardBody>
        </Card>

        {/* Account */}
        <Card>
          <CardHeader>
            <Text fontSize="lg" color="text.primary">Account</Text>
          </CardHeader>
          <CardBody>
            {loading ? (
              <Flex direction="column" gap="3">
                {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={20} />)}
              </Flex>
            ) : account ? (
              <Flex direction="column" gap="3">
                <Row label="Portfolio value">
                  <Text
                    fontSize="13px"
                    lineHeight="18px"
                    fontFamily="mono"
                    color="text.primary"
                    fontWeight={600}
                  >
                    {formatGbp(account.total)}
                  </Text>
                </Row>
                <Row label="Cash available">
                  <Text
                    fontSize="13px"
                    lineHeight="18px"
                    fontFamily="mono"
                    color="text.primary"
                  >
                    {formatGbp(account.cash)}
                  </Text>
                </Row>
                <Row label="Open P&L">
                  <Text
                    fontSize="13px"
                    lineHeight="18px"
                    fontFamily="mono"
                    color={account.ppl >= 0 ? 'status.success.text' : 'status.error.text'}
                  >
                    {account.ppl >= 0 ? '+' : ''}{formatGbp(account.ppl)}
                  </Text>
                </Row>
                <Row label="Realised result">
                  <Text
                    fontSize="13px"
                    lineHeight="18px"
                    fontFamily="mono"
                    color={account.result >= 0 ? 'status.success.text' : 'status.error.text'}
                  >
                    {account.result >= 0 ? '+' : ''}{formatGbp(account.result)}
                  </Text>
                </Row>
              </Flex>
            ) : (
              <Text fontSize="sm" color="text.tertiary">Unable to fetch account data</Text>
            )}
          </CardBody>
        </Card>

        {/* Portfolio Goal */}
        <Card>
          <CardHeader>
            <Flex align="center" gap="2">
              <Text fontSize="lg" color="text.primary">Portfolio Goal</Text>
              {goal && <Badge variant="brand">Active</Badge>}
            </Flex>
          </CardHeader>
          <CardBody>
            <Text fontSize="sm" color="text.secondary" mb="3">
              Set a portfolio value target. Claude will factor this into daily scan analysis,
              suggesting strategies to make progress while keeping risk in check.
            </Text>

            <Flex direction="column" gap="3">
              <Flex justify="space-between" align="center">
                <Text as="label" fontSize="sm" color="text.secondary">Target value</Text>
                <Flex align="center" gap="2">
                  <Text fontSize="sm" color="text.tertiary">£</Text>
                  <chakra.input
                    type="number"
                    min={100}
                    step={1000}
                    value={editGoalTarget}
                    onChange={(e) => setEditGoalTarget(e.target.value)}
                    w="110px"
                    p="4px 8px"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="border.primary"
                    bg="bg.secondary"
                    color="text.primary"
                    fontSize={{ base: '16px', md: '13px' }}
                    fontFamily="mono"
                    textAlign="right"
                  />
                </Flex>
              </Flex>

              <Flex justify="space-between" align="center">
                <Text as="label" fontSize="sm" color="text.secondary">Target date</Text>
                <chakra.input
                  type="date"
                  value={editGoalDate}
                  onChange={(e) => setEditGoalDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  w="160px"
                  p="4px 8px"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="border.primary"
                  bg="bg.secondary"
                  color="text.primary"
                  fontSize={{ base: '16px', md: '13px' }}
                />
              </Flex>

              {goal && (
                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" color="text.tertiary">
                    Started at {formatGbp(goal.startingValue)} on{' '}
                    {new Date(goal.setAt).toLocaleDateString('en-GB')}
                  </Text>
                </Flex>
              )}

              <Flex gap="2">
                <Button
                  onClick={saveGoal}
                  loading={goalSaving}
                  variant="primary"
                  px="3" py="2" fontSize="13px"
                >
                  {goal ? 'Update Goal' : 'Set Goal'}
                </Button>
                {goal && (
                  <Button
                    onClick={clearGoal}
                    loading={goalSaving}
                    variant="ghost"
                    px="3" py="2" fontSize="13px"
                  >
                    Clear Goal
                  </Button>
                )}
              </Flex>
            </Flex>
          </CardBody>
        </Card>

        {/* Auto-Trade */}
        <Card>
          <CardHeader>
            <Flex align="center" gap="2">
              <Text fontSize="lg" color="text.primary">Auto-Trade</Text>
              {autoTrade?.enabled && <Badge variant="success">Active</Badge>}
            </Flex>
          </CardHeader>
          <CardBody>
            <Flex direction="column" gap="3">
              <Text fontSize="sm" color="text.secondary" m={0}>
                When enabled, the engine automatically places market orders on your {isDemo ? 'demo' : 'live'} account
                based on decision card recommendations. Buy, reduce, and exit signals are executed automatically.
                Hold signals are dismissed.
              </Text>

              {!isDemo && (
                <Box
                  p="3"
                  bg="status.error.bg"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="status.error.border"
                >
                  <Text fontSize="sm" color="status.error.text" m={0}>
                    Auto-trade is only available in demo mode. Switch to demo mode to enable.
                  </Text>
                </Box>
              )}

              {autoTrade && isDemo && (
                <>
                  <Flex justify="space-between" align="center">
                    <Text as="label" fontSize="sm" color="text.secondary">Max position size</Text>
                    <Flex align="center" gap="2">
                      <chakra.input
                        type="number"
                        min={1}
                        max={100}
                        step={1}
                        value={editMaxPos}
                        onChange={(e) => setEditMaxPos(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                        w="72px"
                        p="4px 8px"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="border.primary"
                        bg="bg.secondary"
                        color="text.primary"
                        fontSize={{ base: '16px', md: '13px' }}
                        fontFamily="mono"
                        textAlign="right"
                      />
                      <Text fontSize="sm" color="text.tertiary">units</Text>
                    </Flex>
                  </Flex>

                  <Flex justify="space-between" align="center">
                    <Text as="label" fontSize="sm" color="text.secondary">Risk per trade</Text>
                    <Flex align="center" gap="2">
                      <chakra.input
                        type="number"
                        min={0.1}
                        max={5.0}
                        step={0.1}
                        value={editRisk}
                        onChange={(e) => setEditRisk(Math.max(0.1, Math.min(5.0, parseFloat(e.target.value) || 0.1)))}
                        w="72px"
                        p="4px 8px"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="border.primary"
                        bg="bg.secondary"
                        color="text.primary"
                        fontSize={{ base: '16px', md: '13px' }}
                        fontFamily="mono"
                        textAlign="right"
                      />
                      <Text fontSize="sm" color="text.tertiary">%</Text>
                    </Flex>
                  </Flex>

                  {paramsChanged && (
                    <Button
                      onClick={saveTradeParams}
                      loading={saving}
                      variant="primary"
                      px="3" py="2" fontSize="13px"
                    >
                      Save parameters
                    </Button>
                  )}

                  <Button
                    onClick={toggleAutoTrade}
                    loading={toggling}
                    variant={autoTrade.enabled ? 'ghost' : 'primary'}
                    mt="2"
                  >
                    {autoTrade.enabled ? 'Disable Auto-Trade' : 'Enable Auto-Trade'}
                  </Button>

                  {/* Re-enable guardrail: enabling must be an informed, explicit choice */}
                  <Dialog.Root open={confirmEnableOpen} onOpenChange={(e) => setConfirmEnableOpen(e.open)}>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                      <Dialog.Content
                        bg="surface.primary"
                        border="1px solid"
                        borderColor="border.primary"
                        borderRadius="lg"
                        p="5"
                        maxW="420px"
                        w="90vw"
                      >
                        <Dialog.Title fontSize="16px" fontWeight="600" color="text.primary" mb="2">
                          Re-enable auto-trade?
                        </Dialog.Title>
                        <Text fontSize="sm" color="text.secondary" mb="3">
                          Auto-trade was deliberately disabled after a failed measurement week
                          (13 Jul 2026). Safety exits have continued to manage open positions
                          while it has been off.
                        </Text>
                        <Text fontSize="sm" color="text.secondary" mb="4">
                          Re-enabling resumes automatic buying on your demo account from the
                          next market cron run (Mon&ndash;Fri, 14:00&ndash;16:55 and
                          19:00&ndash;20:55 UTC).
                        </Text>
                        <Flex gap="2" justify="flex-end">
                          <Button variant="ghost" onClick={() => setConfirmEnableOpen(false)}>
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            loading={toggling}
                            onClick={performAutoTradeToggle}
                          >
                            Yes, resume auto-trading
                          </Button>
                        </Flex>
                        <Dialog.CloseTrigger position="absolute" top="3" right="3" />
                      </Dialog.Content>
                    </Dialog.Positioner>
                  </Dialog.Root>
                </>
              )}

              {loading && (
                <Skeleton height={36} />
              )}
            </Flex>
          </CardBody>
        </Card>

        {/* Model Configuration */}
        <Card>
          <CardHeader>
            <Flex align="center" gap="2">
              <Text fontSize="lg" color="text.primary">Model Configuration</Text>
              <Badge variant="neutral" size="sm">Read-only</Badge>
            </Flex>
          </CardHeader>
          <CardBody>
            <Text fontSize="sm" color="text.secondary" mb="3">
              Engine parameters driving regime detection, risk scoring, and opportunity assessment.
              Tap the <strong>?</strong> icon on any parameter for an AI-powered explanation.
            </Text>

            <Flex direction="column" gap="1px">
              {MODEL_CATEGORIES.map((cat) => {
                const isExpanded = expandedCategories.has(cat.title);
                return (
                  <Box key={cat.title}>
                    <Box
                      as="button"
                      onClick={() => toggleCategory(cat.title)}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      w="100%"
                      p="3"
                      bg="bg.secondary"
                      border="none"
                      borderRadius={isExpanded ? '8px 8px 0 0' : 'md'}
                      cursor="pointer"
                      mt="2"
                    >
                      <Text fontSize="sm" color="text.primary" fontWeight={600}>
                        {cat.title}
                      </Text>
                      <Text fontSize="11px" color="text.tertiary">
                        {isExpanded ? '\u25B2' : '\u25BC'} {cat.params.length} params
                      </Text>
                    </Box>

                    {isExpanded && (
                      <Box
                        bg="bg.secondary"
                        borderRadius="0 0 8px 8px"
                        px="3" pb="3" pt="0"
                      >
                        {cat.params.map((p) => (
                          <Flex
                            key={p.key}
                            justify="space-between"
                            align="center"
                            py="2"
                            borderBottom="1px solid"
                            borderColor="border.secondary"
                          >
                            <Text fontSize="sm" color="text.secondary" flex={1}>
                              {p.label}
                            </Text>
                            <Flex align="center" gap="2">
                              <Text
                                fontSize="13px"
                                lineHeight="18px"
                                fontFamily="mono"
                                color="text.primary"
                              >
                                {p.value}
                              </Text>
                              <Box
                                as="button"
                                onClick={() => explain(p.key, p.label, p.value, cat.title)}
                                title={`Explain: ${p.label}`}
                                w="22px"
                                h="22px"
                                borderRadius="50%"
                                border="1px solid"
                                borderColor="border.primary"
                                bg="transparent"
                                color="text.tertiary"
                                fontSize="12px"
                                fontWeight={700}
                                cursor="pointer"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                flexShrink={0}
                              >
                                ?
                              </Box>
                            </Flex>
                          </Flex>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Flex>
          </CardBody>
        </Card>
        {/* Scan Schedule & EDGAR */}
        <Card>
          <CardHeader>
            <Flex align="center" gap="2">
              <Text fontSize="lg" color="text.primary">Scan Schedule</Text>
              <Badge variant="neutral" size="sm">Read-only</Badge>
            </Flex>
          </CardHeader>
          <CardBody>
            <Text fontSize="sm" color="text.secondary" mb="3">
              How Market Buddy polls market data and scans SEC EDGAR for insider transactions.
              Market data runs every 5 minutes during trading hours. The EDGAR scanner
              runs once daily after US market close, looking for Form 4 insider purchase filings.
            </Text>

            <Flex direction="column" gap="1px">
              {SCAN_CATEGORIES.map((cat) => {
                const isExpanded = expandedCategories.has(cat.title);
                return (
                  <Box key={cat.title}>
                    <Box
                      as="button"
                      onClick={() => toggleCategory(cat.title)}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      w="100%"
                      p="3"
                      bg="bg.secondary"
                      border="none"
                      borderRadius={isExpanded ? '8px 8px 0 0' : 'md'}
                      cursor="pointer"
                      mt="2"
                    >
                      <Text fontSize="sm" color="text.primary" fontWeight={600}>
                        {cat.title}
                      </Text>
                      <Text fontSize="11px" color="text.tertiary">
                        {isExpanded ? '\u25B2' : '\u25BC'} {cat.params.length} params
                      </Text>
                    </Box>

                    {isExpanded && (
                      <Box
                        bg="bg.secondary"
                        borderRadius="0 0 8px 8px"
                        px="3" pb="3" pt="0"
                      >
                        {cat.params.map((p) => (
                          <Flex
                            key={p.key}
                            justify="space-between"
                            align="center"
                            py="2"
                            borderBottom="1px solid"
                            borderColor="border.secondary"
                          >
                            <Text fontSize="sm" color="text.secondary" flex={1}>
                              {p.label}
                            </Text>
                            <Text
                              fontSize="13px"
                              lineHeight="18px"
                              fontFamily="mono"
                              color="text.primary"
                              textAlign="right"
                              flex={1}
                            >
                              {p.value}
                            </Text>
                          </Flex>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Flex>
          </CardBody>
        </Card>
        {/* Reset Practice Account */}
        <Card>
          <CardHeader>
            <Flex align="center" gap="2">
              <Text fontSize="lg" color="text.primary">Reset Practice Account</Text>
              <Badge variant="error" size="sm">Destructive</Badge>
            </Flex>
          </CardHeader>
          <CardBody>
            <Text fontSize="sm" color="text.secondary" mb="3">
              Wipes all Market Buddy data: watchlist, decision cards, audit log, insider data,
              cached candles, and scan results. Use this after resetting your Trading 212 practice
              account to start fresh. Your login credentials and portfolio goal are preserved.
            </Text>

            {!confirmReset ? (
              <Button
                onClick={() => setConfirmReset(true)}
                variant="danger"
              >
                Reset All Data
              </Button>
            ) : (
              <Flex direction="column" gap="3">
                <Box
                  p="3"
                  bg="status.error.bg"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="status.error.border"
                >
                  <Text fontSize="sm" color="status.error.text" m={0}>
                    This will permanently delete all trading data, watchlist items, decision history,
                    and cached market data. This cannot be undone.
                  </Text>
                </Box>
                <Flex gap="2">
                  <Button
                    onClick={resetAccount}
                    loading={resetting}
                    variant="danger"
                  >
                    Confirm Reset
                  </Button>
                  <Button
                    onClick={() => setConfirmReset(false)}
                    variant="ghost"
                  >
                    Cancel
                  </Button>
                </Flex>
              </Flex>
            )}
          </CardBody>
        </Card>
      </Flex>

      {/* AI Explanation Dialog */}
      <ExplainDialog
        open={explainOpen}
        onOpenChange={setExplainOpen}
        label={explainParam?.label ?? ''}
        value={explainParam?.value ?? ''}
        category={explainParam?.category ?? ''}
        explanation={explainText}
        loading={explainLoading}
      />
    </Box>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Flex justify="space-between" align="center">
      <Text fontSize="sm" color="text.secondary">{label}</Text>
      {children}
    </Flex>
  );
}

function formatGbp(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(value);
}
